import React from 'react';
import { Empty, Spin } from 'antd';
import { Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import moment from 'moment';

const CallVolumeChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" tip="Loading chart data..." />
      </div>
    );
  }

  // Ensure data is an array
  const chartArray = Array.isArray(data) ? data : [];

  if (chartArray.length === 0) {
    return (
      <Empty
        description="No data available. Import CSV files and aggregate data to see the chart."
        style={{ padding: '80px 0' }}
      />
    );
  }

  // Transform data for chart
  const chartData = chartArray.map(item => ({
    date: moment(item.date).format('MMM DD'),
    fullDate: moment(item.date).format('YYYY-MM-DD'),
    campaign: item.campaignName,
    ringCentral: item.ringCentralCalls || 0,
    googleAds: item.googleAdsCalls || 0,
    discrepancy: Math.abs(item.callDiscrepancy || 0),
    discrepancyPct: Math.abs(item.discrepancyPercentage || 0)
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'rgba(255, 255, 255, 0.96)',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{data.fullDate}</p>
          {data.campaign && (
            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '12px' }}>
              {data.campaign}
            </p>
          )}
          <p style={{ margin: '4px 0', color: '#1890ff' }}>
            <strong>RingCentral:</strong> {data.ringCentral} calls
          </p>
          <p style={{ margin: '4px 0', color: '#52c41a' }}>
            <strong>Google Ads:</strong> {data.googleAds} calls
          </p>
          <p style={{ margin: '4px 0', color: data.discrepancy > 0 ? '#ff4d4f' : '#999' }}>
            <strong>Discrepancy:</strong> {data.discrepancy} calls
            {data.discrepancyPct > 0 && ` (${data.discrepancyPct.toFixed(1)}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            label={{ value: 'Number of Calls', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top"
            height={36}
          />
          
          {/* Discrepancy bars in background */}
          <Bar 
            dataKey="discrepancy" 
            fill="#ffccc7" 
            name="Call Discrepancy"
            opacity={0.5}
          />
          
          {/* Call volume lines */}
          <Line 
            type="monotone" 
            dataKey="ringCentral" 
            stroke="#1890ff" 
            strokeWidth={3}
            name="RingCentral Calls"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            type="monotone" 
            dataKey="googleAds" 
            stroke="#52c41a" 
            strokeWidth={3}
            name="Google Ads Calls"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div style={{ 
        marginTop: '24px', 
        padding: '16px', 
        background: '#fafafa', 
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'space-around',
        textAlign: 'center'
      }}>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
            {chartData.reduce((sum, item) => sum + item.ringCentral, 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>Total RingCentral Calls</div>
        </div>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
            {chartData.reduce((sum, item) => sum + item.googleAds, 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Google Ads Calls</div>
        </div>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
            {chartData.reduce((sum, item) => sum + item.discrepancy, 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Discrepancy</div>
        </div>
        <div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
            {chartData.length}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>Days Analyzed</div>
        </div>
      </div>
    </div>
  );
};

export default CallVolumeChart;
